import { ButtonProps, IconButtonProps, Theme } from "@mui/material";
import { RouteObject } from "react-router-dom";

export type AppTheme = Theme;

export interface AppProviderProps {
  /**
   * The content of the app provider.
   */
  children?: React.ReactNode;
  /**
   * [Theme or themes](https://mui.com/toolpad/core/react-app-provider/#theming) to be used by the app in light/dark mode. A [CSS variables theme](https://mui.com/material-ui/customization/css-theme-variables/overview/) is recommended.
   * @default createTheme()
   */
  theme: AppTheme;
  /**
   * Branding options for the app.
   * @default null
   */
  branding?: Branding | null;
  /**
   * Navigation definition for the app.
   * @default []
   */
  navigation?: Navigation;
  /**
   * Router implementation used inside Toolpad components.
   * @default null
   */
  router: RouteObject[];
  /**
   * Session info about the current user.
   * @default null
   */
  session?: Session | null;
  /**
   * Authentication methods.
   * @default null
   */
  authentication?: Authentication | null;
  /**
   * The window where the application is rendered.
   * This is needed when rendering the app inside an iframe, for example.
   * @default window
   */
  window?: Window;
}

export interface NavigateOptions {
  history?: "auto" | "push" | "replace";
}

export interface Navigate {
  (url: string | URL, options?: NavigateOptions): void;
}

/**
 * Abstract router used by Toolpad components.
 */
export interface Router {
  pathname: string;
  searchParams: URLSearchParams;
  navigate: Navigate;
}

export interface Branding {
  title?: string;
  logo?: React.ReactNode;
}

export interface NavigationBaseItem {
  /**
   * The display name of the item.
   */
  title: string;
  /**
   * The URL or path to navigate to.
   */
  url?: string;
  /**
   * Whether the item is visible in the navigation.
   * @default true
   */
  visible?: boolean;
}

export interface NavigationPageItem extends NavigationBaseItem {
  kind: "page";
  segment: string;
  icon?: React.ReactNode;
  router: RouteObject;
  action?: React.ReactNode;
  /** If true, the navigation item will be excluded from rendered menus. */
  hidden?: boolean;
  children?: Navigation;
  externalRedirect?: boolean;
}

export interface NavigationSubheaderItem {
  kind: "header";
  title: string;
}

export interface NavigationDividerItem {
  kind: "divider";
}

export type NavigationItem =
  | NavigationPageItem
  | NavigationSubheaderItem
  | NavigationDividerItem;

export type Navigation = NavigationItem[];

export interface Session {
  user?: {
    id?: string | null;
    name?: string | null;
    image?: string | null;
    email?: string | null;
  };
}

export interface Authentication {
  checkSession: () => void;
  signIn: () => void;
  signOut: () => void;
}

export interface DashboardLayoutProps {
  /**
   * The content of the dashboard.
   */
  children?: React.ReactNode;
  /**
   * Whether the sidebar should not be collapsible to a mini variant in desktop and tablet viewports.
   * @default false
   */
  disableCollapsibleSidebar?: boolean;
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots?: DashboardLayoutSlots;
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: {
    toolbarActions?: {};
    toolbarAccount?: AccountProps;
  };
}

export interface DashboardLayoutSlots {
  /**
   * The toolbar actions component used in the layout header.
   * @default ToolbarActions
   */
  toolbarActions?: React.JSXElementConstructor<{}>;
  /**
   * The toolbar account component used in the layout header.
   * @default Account
   */
  toolbarAccount?: React.JSXElementConstructor<AccountProps>;
}

export const DEFAULT_LOCALE_TEXT = {
  // Account
  signInLabel: "Sign In",
  signOutLabel: "Sign Out",
};

export interface AccountSlots {
  /**
   * The component used for the sign in button.
   * @default Button
   */
  signInButton?: React.ElementType;
  /**
   * The component used for the sign out button.
   * @default MenuItem
   */
  signOutButton?: React.ElementType;
  /**
   * The component used for the custom menu items.
   * @default null
   */
  menuItems?: React.ElementType;
}

export interface AccountProps {
  /**
   * The components used for each slot inside.
   */
  slots?: AccountSlots;
  /**
   * The props used for each slot inside.
   */
  slotProps?: {
    signInButton?: ButtonProps;
    signOutButton?: ButtonProps;
    iconButton?: IconButtonProps;
  };
  /**
   * The labels for the account component.
   * @default DEFAULT_LOCALE_TEXT
   */
  localeText?: typeof DEFAULT_LOCALE_TEXT;
}
